import React, { useState } from "react";
import {
    Box,
    IconButton,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import MedicalNoteAuditPopup from "./MedicalNoteAuditPopup";
import AiAuditAgreement from "./AiAuditAgreement";
import OptionsPopup from "../../common/OptionsPopup";
import { useAuthSlice } from "../../../redux/slice/authSlice";
import { keyframes } from "@mui/system";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import {
    AuditJob,
    getRecentAiAuditDetailsByIdData,
    setAuditJob,
    uploadPDFForAudit,
} from "../../../redux/slice/AI/AiAuditSlice";
import { hideMainErrorPopup, setMainErrorObj, setSingleAlertObj } from "../../../redux/slice/commonSlice";
import { RootState } from "../../../redux/store";
import { tokens } from "../../../theme";

// Animation keyframes
const moveGradient = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

interface MedicalNoteAuditInlineButtonProps {
    size?: 'small' | 'medium';
    className?: string;
    style?: React.CSSProperties;
    // CS-1595 - Pre-loaded audit data integration
    initialAuditData?: any;
    auditInProgress?: boolean;
    auditCompleted?: boolean;
    uploadedPdfFile?: File | null;
    // Function to fetch file on-demand for already-uploaded files
    onFetchFile?: () => Promise<File | undefined>;
    // CS-1595 - Additional parameters for audit API
    patientId?: number | string | null;
    patientName?: string;
    docType?: string;
    documentId?: number | string | null;
    // CS-1594 - Callback when audit completes
    onAuditComplete?: (documentId: string, auditData: any) => void;
    // CS-XXXX - Callback to close all popups (upload popup + audit popup)
    onCloseAllPopups?: () => void;
    skipAuditStartedWaitContinuePopup?: boolean;
}

const MedicalNoteAuditInlineButton: React.FC<MedicalNoteAuditInlineButtonProps> = ({ 
    size = 'small',
    className,
    style,
    initialAuditData,
    auditInProgress = false,
    auditCompleted = false,
    uploadedPdfFile = null,
    onFetchFile,
    patientId = null,
    patientName,
    docType = "medical notes",
    documentId = null,
    // CS-1594 - Callback when audit completes
    onAuditComplete,
    // CS-XXXX - Callback to close all popups
    onCloseAllPopups,
    skipAuditStartedWaitContinuePopup = false,
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const colors = tokens(theme.palette.mode);
    const { userInfo } = useAuthSlice();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    // Read background audit job state for this document from the slice
    const documentIdKey =
        documentId !== null && documentId !== undefined ? String(documentId) : "";
    const job = useSelector<RootState, AuditJob | undefined>((state) =>
        documentIdKey ? state.aiAuditSlice.auditJobs[documentIdKey] : undefined,
    );

    const [popupOpen, setPopupOpen] = useState(false);
    const [showAgreement, setShowAgreement] = useState(false);
    const [showWaitContinuePopup, setShowWaitContinuePopup] = useState(false);
    
    // CS-1594 - Local state to track audit completion
    const [localAuditData, setLocalAuditData] = useState<any>(initialAuditData);
    const [fetchedAuditData, setFetchedAuditData] = useState<any>(null);

    // Derive button state
    const isUploading = job?.status === "uploading";
    const isProcessing = job?.status === "processing";
    const isInProgress = isUploading || isProcessing;
    const jobCompletedData = job?.status === "completed" ? job.data : null;
    const hasAuditData = !!localAuditData || auditCompleted || !!jobCompletedData;

    // Size configurations
    const sizeConfig = {
        small: { height: 30, collapsedWidth: 36, expandedWidth: 230, iconSize: 18, fontSize: 15, spinnerSize: 16 },
        medium: { height: 56, collapsedWidth: 56, expandedWidth: 280, iconSize: 26, fontSize: 15, spinnerSize: 22 },
    };

    const config = sizeConfig[size];
    
    // CS-1594 - Update local audit data when prop changes
    React.useEffect(() => {
        if (initialAuditData) {
            setLocalAuditData(initialAuditData);
        }
    }, [initialAuditData]);

    const notifiedDocIdRef = React.useRef<string>("");
    React.useEffect(() => {
        if (
            job?.status === "completed" &&
            job.data &&
            documentIdKey &&
            notifiedDocIdRef.current !== documentIdKey
        ) {
            notifiedDocIdRef.current = documentIdKey;
            setLocalAuditData(job.data);
            if (onAuditComplete) {
                onAuditComplete(documentIdKey, job.data);
            }
        }
    }, [job?.status, job?.data, documentIdKey, onAuditComplete]);

    // Reset notification guard if the document key changes
    React.useEffect(() => {
        notifiedDocIdRef.current = "";
    }, [documentIdKey]);

    // CS-1594 - Determine button text based on local audit data
    const buttonText = hasAuditData ? "Medical Note Report" : "Run Medical Note Audit";

    // CS-1594 - Dynamic width based on button text
    const buttonWidth = hasAuditData ? 200 : 230;

    // ── Click handlers ────────────────────────────────────────────────────

    // Open the report popup using the audit ID we already have
    const openExistingReport = async () => {
        const auditId =
            localAuditData?.auditId ||
            localAuditData?.audit_id ||
            jobCompletedData?.audit_id ||
            jobCompletedData?.auditId ||
            job?.auditId;

        if (!auditId) {
            dispatch(setSingleAlertObj({
                message: "Audit ID not found for this document.",
                alertType: "error",
            }));
            return;
        }

        try {
            const response = await dispatch(getRecentAiAuditDetailsByIdData(auditId));

            if (response.meta.requestStatus === "fulfilled") {
                setFetchedAuditData(response.payload?.data);
                setPopupOpen(true);
            } else {
                dispatch(setSingleAlertObj({
                    message: "Failed to load audit details. Please try again.",
                    alertType: "error",
                }));
            }
        } catch (error) {
            dispatch(setSingleAlertObj({
                message: "Error loading audit details. Please try again.",
                alertType: "error",
            }));
        }
    };

    // Kick off a fresh audit: upload then mark job as processing so the
    // polling effect takes over.
    const startNewAudit = async () => {
        if (!documentIdKey) {
            dispatch(setSingleAlertObj({
                message: "Please save the document first before running the audit.",
                alertType: "warning",
            }));
            return;
        }

        // Get a File handle so we have a fileName for the upload form-data.
        let fileToUse: File | null = uploadedPdfFile;
        if (onFetchFile) {
            try {
                const fetched = await onFetchFile();
                if (fetched) fileToUse = fetched;
            } catch (error) {
                dispatch(setSingleAlertObj({
                    message: "Failed to load file. Please try again.",
                    alertType: "error",
                }));
                return;
            }
        }

        if (!fileToUse) {
            dispatch(setSingleAlertObj({
                message: "No file available for audit. Please upload a PDF first.",
                alertType: "warning",
            }));
            return;
        }

        // Mark job as uploading so the icon renders immediately. The
        // fileMetadata snapshot lets CreatePatient restore the dropzone row
        // if the user navigates away with unsaved changes and returns later.
        dispatch(setAuditJob({
            documentId: documentIdKey,
            job: {
                auditId: null,
                status: "uploading",
                data: null,
                error: null,
                fileMetadata: {
                    fileName: fileToUse.name,
                    fileType: fileToUse.type,
                    docType,
                    patientIdAtStart: patientId ?? null,
                    patientNameAtStart: patientName?.trim() || undefined,
                },
            },
        }));

        if (!skipAuditStartedWaitContinuePopup) {
            setShowWaitContinuePopup(true);
        }

        try {
            const result = await dispatch(uploadPDFForAudit({
                file: fileToUse,
                patientId,
                docType,
                fileName: fileToUse.name,
                documentId,
            }));

            if (result.meta.requestStatus !== "fulfilled") {
                const apiErrMsg =
                    typeof result.payload === "string" && result.payload
                        ? result.payload
                        : "Upload failed";
                dispatch(setAuditJob({
                    documentId: documentIdKey,
                    job: { status: "failed", error: apiErrMsg },
                }));
                setShowWaitContinuePopup(false);
                dispatch(setMainErrorObj({
                    title: "An error is occurred while communicating with the server.",
                    errorMessageStream: apiErrMsg,
                }));
                setTimeout(() => {
                    dispatch(hideMainErrorPopup());
                }, 10000);
                return;
            }

            const payload: any = result.payload;
            const auditId =
                payload?.audit_id ||
                payload?.auditId ||
                payload?.responseJson?.audit_id ||
                payload?.data?.responseJson?.audit_id;

            if (!auditId) {
                throw new Error("No audit ID received from server");
            }

            // Move to "processing" — the polling effect now takes over
            dispatch(setAuditJob({
                documentId: documentIdKey,
                job: { auditId, status: "processing" },
            }));
        } catch (error: any) {
            const errMsg = error?.message || "Failed to start audit. Please try again.";
            dispatch(setAuditJob({
                documentId: documentIdKey,
                job: { status: "failed", error: errMsg },
            }));
            dispatch(setSingleAlertObj({ message: errMsg, alertType: "error" }));
            setShowWaitContinuePopup(false);
        }
    };

    const handleIconClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        // While the audit is in progress, opening the popup just shows the
        // graceful "in progress" empty state — no re-fetch, no re-run.
        setPopupOpen(true);
    };

    const handleButtonClick = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const sliceConfirmsCompleted = job?.status === "completed" && !!job?.data;
        const propsConfirmCompleted = auditCompleted && !!localAuditData;

        if (sliceConfirmsCompleted || propsConfirmCompleted) {
            await openExistingReport();
            return;
        }

        await startNewAudit();
    };

    const handleAgreementSigned = () => {
        console.log("Agreement signed successfully");
        // setHasAgreed(true);
        setShowAgreement(false);
        // After signing, proceed with the audit action
        // Re-trigger the button click logic
        setTimeout(() => {
            const syntheticEvent = {
                preventDefault: () => {},
                stopPropagation: () => {},
            } as React.MouseEvent;
            handleButtonClick(syntheticEvent);
        }, 100);
    };

    const handleAgreementCancel = () => {
        console.log("Agreement cancelled");
        setShowAgreement(false);
    };

    const handleClosePopup = () => {
        console.log("Closing popup, resetting state");
        setPopupOpen(false);
        setFetchedAuditData(null);

        if (onCloseAllPopups) {
            onCloseAllPopups();
        }
    };

    // ── Renderers ─────────────────────────────────────────────────────────

    const renderInProgressIcon = () => {
        const tooltipMessage = isUploading
            ? "Submitting medical note for audit. You'll be notified when the report is ready."
            : "Medical Note Audit is being processed. You'll be notified when the report is ready.";

        return (
            <Tooltip title={tooltipMessage} placement="top" arrow>
                <IconButton
                    onClick={handleIconClick}
                    className={className}
                    sx={{
                        width: 35,
                        height: 35,
                        minWidth: 35,
                        minHeight: 35,
                        margin: "10px 5px 5px 5px",
                        padding: 0,
                        flexShrink: 0,
                        alignSelf: "center",
                        boxSizing: "border-box",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "5px",
                        border: `0.5px solid ${colors.primary[300]}`,
                        backgroundColor: isDark
                            ? "rgba(0, 167, 111, 0.20)"
                            : "rgba(0, 167, 111, 0.10)",
                        color: colors.primary[300],
                        transition: "background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
                        "&:hover": {
                            backgroundColor: isDark
                                ? "rgba(0, 167, 111, 0.32)"
                                : "rgba(0, 167, 111, 0.18)",
                            borderColor: colors.primary[300],
                            boxShadow: isDark
                                ? "0 0 0 2px rgba(0, 167, 111, 0.30)"
                                : "0 0 0 2px rgba(0, 167, 111, 0.20)",
                        },
                        ...style,
                    }}
                >
                    <CloudSyncIcon sx={{ fontSize: 20, display: "block" }} />
                </IconButton>
            </Tooltip>
        );
    };

    const renderWideButton = () => {
        const isCompleted = hasAuditData;
        const tooltipMessage = buttonText; // "Medical Note Report" or "Run Medical Note Audit"

        const completedStyles = {
            background: "transparent",
            border: `1px solid ${theme.palette.success.main}`,
            color: theme.palette.success.main,
            transition: "background-color 0.2s ease",
            "&:hover": {
                backgroundColor: isDark
                    ? "rgba(76, 175, 80, 0.14)"
                    : "rgba(76, 175, 80, 0.08)",
                border: `1px solid ${theme.palette.success.main}`,
            },
        } as const;

        const idleGradientBg = isDark
            ? "linear-gradient(135deg, #6366f1, #8b5cf6, #a855f7, #ec4899)"
            : "linear-gradient(135deg, #4f46e5, #7c3aed, #9333ea, #db2777)";
        const idleShadow = isDark
            ? "0 8px 32px rgba(139, 92, 246, 0.4), 0 4px 16px rgba(0, 0, 0, 0.5)"
            : "0 8px 32px rgba(124, 58, 237, 0.3), 0 4px 16px rgba(0, 0, 0, 0.2)";
        const idleStyles = {
            background: idleGradientBg,
            backgroundSize: "300% 300%",
            animation: `${moveGradient} 4s ease infinite`,
            color: "#ffffff",
            boxShadow: idleShadow,
            transition: "filter 0.2s ease, box-shadow 0.2s ease",
            "&:hover": {
                filter: "brightness(1.1)",
                boxShadow: idleShadow,
            },
        } as const;

        const ActionIcon = isCompleted ? VisibilityOutlinedIcon : FactCheckIcon;

        return (
            <Tooltip title={tooltipMessage} placement="top" arrow>
                <IconButton
                    onClick={handleButtonClick}
                    className={className}
                    sx={{
                        width: 35,
                        height: 35,
                        minWidth: 35,
                        minHeight: 35,
                        margin: "10px 5px 5px 5px",
                        padding: 0,
                        flexShrink: 0,
                        alignSelf: "center",
                        boxSizing: "border-box",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "5px",
                        ...(isCompleted ? completedStyles : idleStyles),
                        ...style,
                    }}
                >
                    <ActionIcon sx={{ fontSize: 20, display: "block" }} />
                </IconButton>
            </Tooltip>
        );
    };

    return (
        <>
            {isInProgress ? renderInProgressIcon() : renderWideButton()}

            <MedicalNoteAuditPopup
                open={popupOpen}
                onClose={handleClosePopup}
                initialAuditData={isInProgress ? null : (fetchedAuditData || initialAuditData)}
                auditInProgress={isInProgress || auditInProgress}
                auditCompleted={auditCompleted}
                uploadedPdfFile={null}
                patientId={patientId}
                docType={docType}
                documentId={documentId}
                onAuditComplete={onAuditComplete}
            />

            {/* AI Audit Agreement Dialog */}
            <AiAuditAgreement
                open={showAgreement}
                onAgreementSigned={handleAgreementSigned}
                onCancel={handleAgreementCancel}
            />

            <OptionsPopup
                open={showWaitContinuePopup && !skipAuditStartedWaitContinuePopup}
                onClose={() => setShowWaitContinuePopup(false)}
                variant="confirm"
                title="Medical Note Audit Started"
                message="The medical audit is running in the background. Wait here, or continue with other tasks — you'll be notified when the report is ready."
                buttons={[
                    {
                        name: "Wait",
                        color: "secondary",
                        onClick: () => setShowWaitContinuePopup(false),
                    },
                    {
                        name: "Continue",
                        color: "primary",
                        onClick: () => {
                            setShowWaitContinuePopup(false);
                            navigate("/dashboard");
                        },
                    },
                ]}
            />
        </>
    );
};

export default MedicalNoteAuditInlineButton;
